import {
  type CandidateInfo,
  type CandidateContact,
  type RawSection,
} from "./cv.types";

/**
 * Safely extracts normalized candidate information and contact details
 * by cross-referencing profile attributes and any dedicated "contact" section.
 */
export function extractCandidateInfo(profile: any, sections: RawSection[] = []): CandidateInfo {
  const contactSection = sections.find((s) => s.type === "contact");
  const contactContent = contactSection?.content || {};

  const socialLinks: Array<{ platform: string; url: string; label?: string }> = 
    Array.isArray(contactContent.socialLinks) ? contactContent.socialLinks : [];

  const findSocialUrl = (platformName: string): string | undefined => {
    const link = socialLinks.find(
      (s) => s.platform?.toLowerCase() === platformName.toLowerCase() ||
             s.label?.toLowerCase() === platformName.toLowerCase()
    );
    return link?.url || undefined;
  };

  const contact: CandidateContact = {
    email: contactContent.email || profile?.email || undefined,
    phone: contactContent.phone || profile?.phone || undefined,
    office: contactContent.office || undefined,
    address: contactContent.address || profile?.location || undefined,
    location: contactContent.office || contactContent.address || profile?.location || undefined,
    websiteUrl: contactContent.website || profile?.websiteUrl || undefined,
    googleScholarUrl: findSocialUrl("scholar") || findSocialUrl("googlescholar") || profile?.googleScholarUrl || undefined,
    orcidUrl: findSocialUrl("orcid") || profile?.orcidUrl || undefined,
    researchGateUrl: findSocialUrl("researchgate") || profile?.researchGateUrl || undefined,
    linkedinUrl: findSocialUrl("linkedin") || profile?.linkedinUrl || undefined,
    githubUrl: findSocialUrl("github") || profile?.githubUrl || undefined,
    twitterUrl: findSocialUrl("twitter") || profile?.twitterUrl || undefined,
    socialLinks,
  };

  return {
    fullName: profile?.fullName || "Academic Professional",
    title: profile?.title || null,
    bio: profile?.bio || null,
    photoUrl: profile?.photoUrl || null,
    subdomain: profile?.subdomain || null,
    contact,
  };
}

/**
 * Extracts and normalizes About section content.
 */
export function extractAboutData(content: any, profileBio?: string | null) {
  const body = content?.body || content?.bio || profileBio || "";
  const highlights = Array.isArray(content?.highlights) ? content.highlights : [];
  const heading = content?.heading || "About";
  return { heading, body, highlights };
}

/**
 * Extracts and normalizes Experience / Appointments section content.
 */
export function extractExperienceData(content: any) {
  // Support both schema `items` and legacy `experiences`
  const rawList = Array.isArray(content?.items)
    ? content.items
    : Array.isArray(content?.experiences)
    ? content.experiences
    : [];

  const items = rawList.map((item: any) => ({
    id: item.id || crypto.randomUUID(),
    role: item.role || item.title || "Position",
    organization: item.organization || item.company || item.institution || "Institution",
    department: item.department || undefined,
    startDate: item.startDate || "",
    endDate: item.current ? "Present" : item.endDate || "Present",
    current: Boolean(item.current),
    location: item.location || undefined,
    description: item.description || undefined,
  }));

  const heading = content?.heading || "Academic & Professional Experience";
  return { heading, items };
}

/**
 * Extracts and normalizes Publications section content.
 */
export function extractPublicationsData(content: any) {
  // Support both schema `items` and legacy `publications`
  const rawList = Array.isArray(content?.items)
    ? content.items
    : Array.isArray(content?.publications)
    ? content.publications
    : [];

  const items = rawList.map((pub: any) => ({
    id: pub.id || crypto.randomUUID(),
    title: pub.title || "Untitled Publication",
    authors: pub.authors || "",
    year: pub.year ? String(pub.year) : "",
    venue: pub.venue || pub.journal || pub.conference || undefined,
    type: pub.type || "journal",
    doi: pub.doi || undefined,
    url: pub.url || pub.link || undefined,
    pdfUrl: pub.pdfUrl || undefined,
    abstract: pub.abstract || undefined,
    citationCount: pub.citationCount || undefined,
  }));

  // Sort publications chronologically descending by year if possible
  items.sort((a: any, b: any) => {
    const yearA = parseInt(a.year, 10) || 0;
    const yearB = parseInt(b.year, 10) || 0;
    return yearB - yearA;
  });

  const heading = content?.heading || "Publications";
  return { heading, items };
}

/**
 * Extracts and normalizes Research Interests section content.
 */
export function extractResearchData(content: any) {
  const summary = content?.summary || undefined;
  const rawAreas = Array.isArray(content?.areas) ? content.areas : [];

  const areas = rawAreas.map((area: any) => ({
    id: area.id || crypto.randomUUID(),
    title: area.title || "Research Area",
    description: area.description || undefined,
    keywords: Array.isArray(area.keywords) ? area.keywords : [],
  }));

  const heading = content?.heading || "Research Interests";
  return { heading, summary, areas };
}

/**
 * Extracts and normalizes Teaching section content.
 */
export function extractTeachingData(content: any) {
  const rawList = Array.isArray(content?.items) ? content.items : [];

  const items = rawList.map((item: any) => ({
    id: item.id || crypto.randomUUID(),
    courseCode: item.courseCode || undefined,
    courseTitle: item.courseTitle || item.title || "Course",
    level: item.level || "undergraduate",
    role: item.role || "instructor",
    institution: item.institution || undefined,
    term: item.term || undefined,
    description: item.description || undefined,
    courseUrl: item.courseUrl || item.syllabusUrl || undefined,
  }));

  const heading = content?.heading || "Teaching Experience";
  return { heading, items };
}

/**
 * Extracts and normalizes Grants & Projects section content.
 */
export function extractProjectsData(content: any) {
  const rawList = Array.isArray(content?.items) ? content.items : [];

  const items = rawList.map((item: any) => ({
    id: item.id || crypto.randomUUID(),
    title: item.title || "Project",
    role: item.role || "pi",
    fundingAgency: item.fundingAgency || item.funder || undefined,
    grantNumber: item.grantNumber || undefined,
    amount: item.amount || undefined,
    startDate: item.startDate || undefined,
    endDate: item.isCurrent ? "Present" : item.endDate || undefined,
    isCurrent: Boolean(item.isCurrent),
    collaborators: item.collaborators || undefined,
    description: item.description || undefined,
    projectUrl: item.projectUrl || undefined,
  }));

  const heading = content?.heading || "Grants & Funded Projects";
  return { heading, items };
}

/**
 * Extracts and normalizes Skills & Methodologies section content.
 */
export function extractSkillsData(content: any) {
  const rawGroups = Array.isArray(content?.groups) ? content.groups : [];

  const groups = rawGroups.map((group: any) => ({
    id: group.id || crypto.randomUUID(),
    categoryName: group.categoryName || "Skills",
    skills: Array.isArray(group.skills)
      ? group.skills.map((s: any) => (typeof s === "string" ? { name: s } : s))
      : [],
  }));

  const heading = content?.heading || "Skills & Methodologies";
  return { heading, groups };
}
